<template>
  <div class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden">
    <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <div>
          <div class="flex items-center gap-2">
            <ChartBarIcon class="h-5 w-5 text-blue-500" />
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              {{
                drilldownTeam
                  ? `Gewogen omzet — ${drilldownTeam.team_name}`
                  : 'Gewogen omzet per Team'
              }}
            </h3>
            <button
              @click="showTeamInfo = !showTeamInfo"
              class="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
              title="Uitleg over de teamgrafiek"
            >
              <InformationCircleIcon class="h-5 w-5" />
            </button>
          </div>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{
              drilldownTeam
                ? 'Gewogen omzet per medewerker per fase'
                : 'Gewogen omzet per team per fase (klik op een team voor details)'
            }}
          </p>
        </div>
        <button
          v-if="drilldownTeam"
          @click="drilldownTeam = null"
          class="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
        >
          <ArrowLeftIcon class="h-4 w-4" />
          Terug
        </button>
      </div>
    </div>

    <InfoBox v-if="showTeamInfo" title="Hoe werkt deze grafiek?" color="blue">
      <p>
        De grafiek toont de <strong>gewogen omzet</strong> per team, uitgesplitst over de salesfases
        Verkenning, Lead en Prospect. De gewogen omzet is de dealwaarde vermenigvuldigd met de
        ML-voorspelde winkans.
      </p>
      <p>
        <strong>Klik op een team</strong> om in te zoomen naar de individuele medewerkers binnen dat
        team. Gebruik de "Terug" knop om terug te keren naar het teamoverzicht.
      </p>
    </InfoBox>

    <div class="p-5">
      <VChart
        :option="chartOption"
        :notMerge="true"
        autoresize
        style="width: 100%; height: 350px"
        @click="handleClick"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { ChartBarIcon, ArrowLeftIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import InfoBox from './InfoBox.vue'

use([BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const props = defineProps({
  data: { type: Object, required: true }
})

const drilldownTeam = ref(null)
const showTeamInfo = ref(false)

const isDarkMode = ref(false)

const updateDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

let observer = null

onMounted(() => {
  updateDarkMode()
  observer = new MutationObserver(updateDarkMode)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const TEAM_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#06b6d4',
  '#f59e0b',
  '#ef4444',
  '#10b981',
  '#f97316',
  '#84cc16',
  '#e11d48',
  '#14b8a6'
]

const EMPLOYEE_COLORS = [
  '#60a5fa',
  '#a78bfa',
  '#22d3ee',
  '#fbbf24',
  '#f87171',
  '#34d399',
  '#fb923c',
  '#a3e635',
  '#fb7185',
  '#2dd4bf',
  '#93c5fd',
  '#c4b5fd',
  '#67e8f9',
  '#fcd34d'
]

const stages = ['Verkenning', 'Lead', 'Prospect']

function formatCurrency(amount) {
  if (!amount) return '€ 0'
  return `€ ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

const chartOption = computed(() => {
  const dark = isDarkMode.value
  const colors = {
    textPrimary: dark ? '#e5e7eb' : '#374151',
    textSecondary: dark ? '#9ca3af' : '#6b7280',
    border: dark ? '#4b5563' : '#e5e7eb',
    tooltipBg: dark ? '#1f2937' : '#ffffff',
    tooltipBorder: dark ? '#374151' : '#e5e7eb'
  }

  if (drilldownTeam.value) {
    return buildEmployeeChart(drilldownTeam.value, colors)
  }
  return buildTeamChart(colors)
})

function buildTooltipConfig(colors, formatter) {
  return {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    appendToBody: true,
    backgroundColor: colors.tooltipBg,
    borderColor: colors.tooltipBorder,
    textStyle: { color: colors.textPrimary, fontSize: 12 },
    extraCssText: 'z-index: 9999;',
    position: (point, params, dom, rect, size) => {
      const chartRect = rect || { x: 0, y: 0 }
      const viewportX = chartRect.x + point[0]
      const viewportY = chartRect.y + point[1]
      const x = viewportX + 15
      const y = Math.max(10, viewportY - size.contentSize[1] / 2)
      const maxX = window.innerWidth - size.contentSize[0] - 20
      return [Math.min(x, maxX), y]
    },
    formatter
  }
}

function buildPipelineTooltipFormatter(colors) {
  return params => {
    if (!params?.length) return ''
    const stageName = params[0].axisValue
    const totalWeighted = params.reduce((s, p) => s + (Number(p.value) || 0), 0)
    let html = `<div style="font-weight:600;margin-bottom:6px;">${stageName}</div>`
    for (const p of params) {
      if (!p.value) continue
      const pct = totalWeighted > 0 ? Math.round((p.value / totalWeighted) * 100) : 0
      html += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;">
        <span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:2px;"></span>
        <span>${p.seriesName}</span>
        <span style="margin-left:auto;font-weight:500;">${formatCurrency(p.value)} (${pct}%)</span>
      </div>`
    }
    html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${colors.border};font-weight:600;">Totaal: ${formatCurrency(totalWeighted)}</div>`
    return html
  }
}

function buildTeamChart(colors) {
  const teams = props.data.teams
  const series = teams.map((team, i) => ({
    name: team.team_name,
    type: 'bar',
    stack: 'total',
    data: [
      team.subtotals.verkenning_weighted,
      team.subtotals.lead_weighted,
      team.subtotals.prospect_weighted
    ],
    itemStyle: { color: TEAM_COLORS[i % TEAM_COLORS.length] },
    emphasis: { focus: 'series' }
  }))

  return {
    animation: true,
    grid: { left: 80, right: 30, top: 50, bottom: 40 },
    tooltip: buildTooltipConfig(colors, buildPipelineTooltipFormatter(colors)),
    legend: {
      top: 0,
      type: 'scroll',
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      pageTextStyle: { color: colors.textSecondary },
      pageIconColor: colors.textSecondary,
      pageIconInactiveColor: colors.border
    },
    xAxis: {
      type: 'category',
      data: stages,
      axisLabel: { color: colors.textPrimary, fontSize: 12 },
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { alignWithLabel: true, lineStyle: { color: colors.border } }
    },
    yAxis: {
      type: 'value',
      name: 'Gewogen omzet',
      nameTextStyle: { fontSize: 11, color: colors.textSecondary },
      axisLabel: {
        color: colors.textSecondary,
        fontSize: 11,
        formatter: v => {
          if (v >= 1000000) return `€${(v / 1000000).toFixed(1)}M`
          if (v >= 1000) return `€${(v / 1000).toFixed(0)}K`
          return `€${v}`
        }
      },
      axisLine: { lineStyle: { color: colors.border } },
      splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
    },
    series
  }
}

function buildEmployeeChart(team, colors) {
  const employees = team.employees
  const series = employees.map((emp, i) => ({
    name: emp.employee_name,
    type: 'bar',
    stack: 'total',
    data: [emp.verkenning_weighted, emp.lead_weighted, emp.prospect_weighted],
    itemStyle: { color: EMPLOYEE_COLORS[i % EMPLOYEE_COLORS.length] },
    emphasis: { focus: 'series' }
  }))

  return {
    animation: true,
    grid: { left: 80, right: 30, top: 50, bottom: 40 },
    tooltip: buildTooltipConfig(colors, buildPipelineTooltipFormatter(colors)),
    legend: {
      top: 0,
      type: 'scroll',
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      pageTextStyle: { color: colors.textSecondary },
      pageIconColor: colors.textSecondary,
      pageIconInactiveColor: colors.border
    },
    xAxis: {
      type: 'category',
      data: stages,
      axisLabel: { color: colors.textPrimary, fontSize: 12 },
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { alignWithLabel: true, lineStyle: { color: colors.border } }
    },
    yAxis: {
      type: 'value',
      name: 'Gewogen omzet',
      nameTextStyle: { fontSize: 11, color: colors.textSecondary },
      axisLabel: {
        color: colors.textSecondary,
        fontSize: 11,
        formatter: v => {
          if (v >= 1000000) return `€${(v / 1000000).toFixed(1)}M`
          if (v >= 1000) return `€${(v / 1000).toFixed(0)}K`
          return `€${v}`
        }
      },
      axisLine: { lineStyle: { color: colors.border } },
      splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
    },
    series
  }
}

function handleClick(params) {
  if (drilldownTeam.value) return
  if (params.componentType !== 'series') return
  const teamName = params.seriesName
  const team = props.data.teams.find(t => t.team_name === teamName)
  if (team) {
    drilldownTeam.value = team
  }
}
</script>
